import { useEffect, useMemo, useState } from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import { APPS, AppIcon, searchApps } from '@hana/ui';
import { api, currentAccount, signOut } from '../auth';
import { IC, Stroke } from './Icons';
import { ThemeButton } from './Login';
import { AuthShell } from './Shell';
import s from './Home.module.css';

// Hub sau đăng nhập — board Mint-Hub view `apps`: chào, tìm vùng, lưới vùng, Mở gần đây, Thông báo chung.
// Số đếm trên thẻ, "Mở gần đây" và thông báo chưa có nguồn dữ liệu → hiện trạng thái rỗng của board.

const DATE = new Intl.DateTimeFormat('vi-VN', { weekday: 'long' });
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function today(): string {
  const d = new Date();
  const w = DATE.format(d);
  return `${w.charAt(0).toUpperCase()}${w.slice(1)}, ${String(d.getDate()).padStart(2, '0')}/${MON[d.getMonth()]}/${d.getFullYear()}`;
}
const initials = (n: string) =>
  n
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';

export function Home() {
  const [me, setMe] = useState<AccountInfo | null>(null);
  const [q, setQ] = useState('');
  const [menu, setMenu] = useState(false);
  const [apiErr, setApiErr] = useState(false);
  const [busy, setBusy] = useState(true);

  const check = () => {
    setBusy(true);
    api('/api/v1/me')
      .then(() => setApiErr(false))
      .catch(() => setApiErr(true))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    void currentAccount().then(setMe);
    check();
  }, []);

  const apps = useMemo(() => searchApps(q, APPS), [q]);
  const name = me?.name || me?.username || '';
  const mail = me?.username ?? '';

  return (
    <AuthShell
      greet={
        <div className={s.greetH}>
          <div className={s.date}>{today()}</div>
          <b className={s.hello}>Chào {name}</b>
        </div>
      }
      top={
        <>
          <span className={s.theme}>
            <ThemeButton />
          </span>
          <button type="button" className={s.bell} aria-label="Thông báo chung">
            <Stroke d={IC.bell} size={20} />
          </button>
          <div className={s.um}>
            <button type="button" className={s.user} aria-label={`Tài khoản ${name}`} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
              <span className={s.ini}>{initials(name)}</span>
              <span className={s.uname}>{name}</span>
            </button>
            {menu && (
              <>
                <div className={s.scrim} onClick={() => setMenu(false)} />
                <div role="menu" aria-label="Tài khoản" className={s.menu}>
                  <div className={s.menuHead}>
                    <b>{name}</b>
                    <div>{mail}</div>
                  </div>
                  <a role="menuitem" className={s.mi} href="/mee/">
                    Hồ sơ của tôi · HANA Mee
                  </a>
                  <button role="menuitem" type="button" className={`${s.mi} ${s.miErr}`} onClick={() => void signOut()}>
                    Đăng xuất mọi app
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      }
    >
      <main className={s.main} aria-labelledby="hbH">
        <section className={s.col}>
          <div className={s.head}>
            <div className={s.date}>{today()}</div>
            <h1 id="hbH" className={s.hello}>
              Chào {name}
            </h1>
          </div>
          {apiErr && (
            <div role="alert" className={s.alert}>
              <b>Chưa lấy được số mới từ các app</b>
              <button type="button" className={s.retry} onClick={check} disabled={busy}>
                Thử lại
              </button>
            </div>
          )}
          <label className={s.search}>
            <Stroke d={IC.q} size={16} w={2} />
            <input aria-label="Tìm vùng" placeholder="Tìm vùng · ví dụ mail, lương, chat" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          {!apps.length && (
            <div role="status" className={s.none}>
              Không có vùng khớp “{q}”
            </div>
          )}
          <div role="list" aria-label="Chọn vùng" className={s.grid}>
            {apps.map((a) => (
              <a key={a.key} role="listitem" href={a.path} className={s.tile} aria-label={a.name}>
                <div className={s.tileTop}>
                  <AppIcon app={a.key} size={64} />
                </div>
                <div>
                  <b className={s.tileName}>{a.name}</b>
                  <div className={s.tileVal}>{a.desc}</div>
                </div>
                <span className={s.go} aria-hidden="true">
                  <Stroke d={IC.arrow} />
                </span>
              </a>
            ))}
          </div>
          <div className={s.rule}>
            <b>Mở gần đây</b>
            <span />
          </div>
          <div className={s.empty}>Chưa mở gì gần đây</div>
        </section>
        <aside className={s.news} aria-labelledby="hbN">
          <h2 id="hbN" className={s.newsH}>
            Thông báo chung
          </h2>
          <div className={s.noNews}>Không có thông báo mới</div>
        </aside>
      </main>
    </AuthShell>
  );
}

